import type { NextPage } from "next";
import Head from "next/head";

import NextImage from "next/legacy/image";

import { FAQ } from "../components/FAQ/FAQ";
import { Link } from "../components/Actions";
import PlaceholderImage from "../assets/image/Roadmap/couch.png";

import headerDesktop from "../assets/roadmap/headerDesktop.png";
import headerMobile from "../assets/roadmap/headerMobile.png";

import trustlessLogo from "../assets/roadmap/trustlessLogo.png";

import preproduce from "../assets/roadmap/preproduce.png";
import caucus from "../assets/roadmap/caucus.png";
import invite from "../assets/roadmap/invite.png";
import premiere from "../assets/roadmap/premiere.png";
import release from "../assets/roadmap/release.png";
import season2 from "../assets/roadmap/season2.png";

import { CutoutAsset } from "../components/CutoutAsset/CutoutAsset";

import { FadeText } from "../components/Animation/FadeText";

import bg from "../assets/image/Roadmap/bg.png";

import { RoadMapItem } from "../components/RoadMapItem/RoadMapItem";
import SEO from "../components/Seo/Seo";

const prereleaseItems = [
  {
    title: "Form Trustless Media",
    description:
      "Trustless Media is a new production hub that's using membership passes to make shows that couldn’t exist anywhere else. ",
    complete: true,
    image: trustlessLogo,
    link: {
      text: "VISIT US",
      href: "https://www.trustless.media"
    }
  },
  {
    title: "Pre-Produce Coinage",
    description:
      "We've been laying the foundation for Coinage this year, so we can hit the ground running when we form our community. We're setting episode topics for Season 1 now, but we'll be more and more collaborative as we grow.",
    image: preproduce,
    complete: true,
    content: <></>
  },
  {
    title: "Invite our Network",
    description:
      "There’s no access list to get on. We carefully curated a group of rivals from among our seed investors to send out the first few invitations to our Network. Each subsequent person who's invited can get invitations of their own to distribute as they see fit. The process continues until all 1,000 Network membership passes have been minted.",
    image: invite,
    complete: true
  }
];

const season1Items = [
  {
    title: "Release premiere",
    description:
      "Once we start minting our Network membership passes, we'll enter active production. We've set ourselves up to hit the ground running, so you won’t have to wait long for our series launch.",
    complete: true,
    image: premiere
  },
  {
    title: "Release 6 more episodes",
    description:
      "We're thinking of Coinage Season 1 as our pilot season — a chance to work on decentralizing production with our Network and to show our future Caucus what we can do. Season 1 will consist of 7 episodes, released weekly.",
    image: release,
    complete: true,
    content: <></>
  },
  {
    title: "Drop Caucus membership passes",
    description:
      "Toward the end of Season 1, we'll drop our Caucus membership passes.",
    image: caucus,
    complete: true,
    link: {
      text: "Mint",
      href: "/caucus/mint"
    }
  }
];

const season2Items = [
  {
    title: "Co-create Season 2 roadmap",
    description:
      "Now things get really interesting. We need your input. Topics you want to cover. Questions you think need answering. Projects that wouldn't be possible without our collective ambition. Let’s figure out where we go from here, together.",
    complete: false,
    image: season2,
    link: undefined
  },
  {
    title: "Develop Season 2",
    description:
      "This isn’t just a proof of concept for us. We’re building Coinage for the long-run. Expect many more seasons ahead.",
    image: headerDesktop,
    complete: false,
    content: <></>
  }
];

const Roadmap: NextPage = () => {
  return (
    <>
      <SEO title="Roadmap | Coinage Media" />

      <main className="roadmap-main">
        <div className="roadmap-header flex  h-[80vh] tab:h-[100vh]  bg-coinage-orange relative overflow-hidden">
          <div className="hidden tab:flex absolute overflow-hidden z-0 right-0  w-2/3  h-full about-image-holder">
            <CutoutAsset image={headerDesktop} right={false} color="orange" />
          </div>

          <div className="tab:hidden absolute bottom-0 overflow-hidden z-0 right-0  w-full h-[66%] about-image-holder">
            <CutoutAsset image={headerMobile} mobile={true} color="orange" />
          </div>
          <div className=" flex mt-[60px] tab:mt-auto mb-auto z-1 relative mx-[auto] w-full max-w-[93%] xl:max-w-[1200px]">
            <FadeText
              textStyle="display1"
              className="display1 text-coinage-offWhite ">
              OUR
              <br />
              ROADMAP.
            </FadeText>
          </div>
        </div>

        <div className="nav-container bg-white p-4 overflow-x-scroll tab:overflow-hidden">
          <ul className="flex">
            <li className="ml-auto mr-[30px] tab:mr-[120px]">
              <Link
                analyticsProperty="prerelease"
                modifier="tertiary"
                href="#pre">
                Pre-Release
              </Link>
            </li>
            <li className="mr-[30px] tab:mr-[120px]">
              <Link
                analyticsProperty="season 1"
                modifier="tertiary"
                href="#season1">
                Season 1
              </Link>
            </li>
            <li className="mr-auto">
              <Link
                analyticsProperty="season 2"
                modifier="tertiary"
                href="#season2">
                Season 2
              </Link>
            </li>
          </ul>
        </div>

        <div id="pre" className="bg-coinage-offWhite pt-1 tab:pt-20 pb-20">
          <FadeText
            textStyle="display1"
            className="m-4 mb-[30px] tab:mb-[60px] mt-[64px] tab:mt-20 text-center display1">
            PRE-RELEASE.
          </FadeText>

          <div className="max-w-[93%] mx-auto xl:max-w-[1200px]">
            {prereleaseItems.map((item, idx) => (
              <RoadMapItem
                key={idx}
                index={idx}
                image={item.image}
                title={item.title}
                link={item.link}
                description={item.description}
                textLeft={Boolean(idx % 2)}
                complete={item.complete}
                nextComplete={
                  prereleaseItems[idx + 1]?.complete ?? item.complete
                }>
                {item.content}
              </RoadMapItem>
            ))}
          </div>

          <FadeText
            textStyle="display1"
            className="m-4 mt-[64px] mb-[30px] tab:mb-[60px] tab:mt-[100px] text-center display1">
            <span id="season1" className="italic">
              SEASON 1
            </span>{" "}
            RELEASE.
          </FadeText>
          <div className="max-w-[93%] mx-auto xl:max-w-[1200px]">
            {season1Items.map((item, idx) => (
              <RoadMapItem
                key={idx}
                index={idx}
                image={item.image}
                title={item.title}
                link={item.link}
                description={item.description}
                textLeft={!Boolean(idx % 2)}
                complete={item.complete}
                nextComplete={season1Items[idx + 1]?.complete ?? item.complete}>
                {item.content}
              </RoadMapItem>
            ))}
          </div>

          <FadeText
            textStyle="display1"
            className="m-4 mb-[30px] tab:mb-[60px] mt-[64px] tab:mt-[100px] text-center display1">
            <span id="season2" className="italic">
              SEASON 2
            </span>{" "}
            DEVELOPMENT.
          </FadeText>

          <div className="max-w-[93%] mx-auto xl:max-w-[1200px]">
            {season2Items.map((item, idx) => (
              <RoadMapItem
                key={idx}
                image={item.image}
                index={idx}
                title={item.title}
                link={item.link}
                description={item.description}
                textLeft={Boolean(idx % 2)}
                complete={item.complete}
                nextComplete={season2Items[idx + 1]?.complete ?? item.complete}>
                {item.content}
              </RoadMapItem>
            ))}
          </div>
        </div>
        <div id="FAQ" className="bg-coinage-orange pt-[64px] tab:pt-20 pb-20">
          <div className="content-wrapper flex flex-col tab:flex-row m-auto faq-wrapper max-w-[90%] tab:max-w-[1200px]">
            <div className="w-1/2 tab:w-1/3">
              <h2 className="display1 text-coinage-offWhite">FAQS </h2>
            </div>
            <div className="tab:w-2/3">
              <FAQ />
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Roadmap;
